// base64url without padding, hex and Crockford base32 (PROTOCOL.md conventions).

export function toB64u(bytes: Uint8Array): string {
  let s = '';
  for (let i = 0; i < bytes.length; i += 0x8000) s += String.fromCharCode(...bytes.subarray(i, i + 0x8000));
  return btoa(s).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
}

export function fromB64u(text: string): Uint8Array<ArrayBuffer> {
  if (!/^[A-Za-z0-9_-]*$/.test(text)) throw new Error('not base64url');
  const s = atob(text.replace(/-/g, '+').replace(/_/g, '/') + '='.repeat((4 - (text.length % 4)) % 4));
  const out = new Uint8Array(s.length);
  for (let i = 0; i < s.length; i++) out[i] = s.charCodeAt(i);
  return out;
}

export function toHex(bytes: Uint8Array): string {
  return Array.from(bytes, (b) => b.toString(16).padStart(2, '0')).join('');
}

export function fromHex(hex: string): Uint8Array<ArrayBuffer> {
  if (!/^(?:[0-9a-f]{2})*$/.test(hex)) throw new Error('not lowercase hex');
  const out = new Uint8Array(hex.length / 2);
  for (let i = 0; i < out.length; i++) out[i] = parseInt(hex.slice(i * 2, i * 2 + 2), 16);
  return out;
}

const CROCKFORD = '0123456789ABCDEFGHJKMNPQRSTVWXYZ';

/** The first `bits` bits of `bytes`, most significant first, as Crockford base32 (`bits` a multiple of 5). */
export function toCrockford(bytes: Uint8Array, bits = bytes.length * 8): string {
  let out = '';
  for (let i = 0; i < bits; i += 5) {
    let v = 0;
    for (let j = 0; j < 5; j++) {
      const bit = i + j;
      const b = bit < bytes.length * 8 ? (bytes[bit >> 3] >> (7 - (bit & 7))) & 1 : 0;
      v = (v << 1) | b;
    }
    out += CROCKFORD[v];
  }
  return out;
}

/** Decodes Crockford base32 (case-insensitive; O→0, I/L→1; `-` and spaces ignored) to `byteLength` bytes. */
export function fromCrockford(text: string, byteLength: number): Uint8Array<ArrayBuffer> {
  const clean = text.toUpperCase().replace(/[-\s]/g, '').replace(/O/g, '0').replace(/[IL]/g, '1');
  if (clean.length !== Math.ceil((byteLength * 8) / 5)) throw new Error('wrong length');
  const out = new Uint8Array(byteLength);
  let bit = 0;
  for (const ch of clean) {
    const v = CROCKFORD.indexOf(ch);
    if (v < 0) throw new Error(`not Crockford base32: ${ch}`);
    for (let j = 4; j >= 0; j--, bit++) {
      if (bit < byteLength * 8 && (v >> j) & 1) out[bit >> 3] |= 1 << (7 - (bit & 7));
    }
  }
  return out;
}

export function concat(...parts: Uint8Array[]): Uint8Array<ArrayBuffer> {
  const out = new Uint8Array(parts.reduce((n, p) => n + p.length, 0));
  let at = 0;
  for (const p of parts) {
    out.set(p, at);
    at += p.length;
  }
  return out;
}

export const utf8 = (s: string): Uint8Array<ArrayBuffer> => new TextEncoder().encode(s) as Uint8Array<ArrayBuffer>;
